import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  clearApiAuth,
  getStoredToken,
  setApiAuth,
  setupApiClient,
} from '@api/api-client-config';
import {
  clearStoredAttribution,
  getStoredAttribution,
} from '@api/attribution';
import { authGetCurrentUser } from '@api/generated/sdk.gen';
import { googleLogin } from '@api/google-login';
import type { AuthUser, AuthState, AuthContextValue } from './auth.types';

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
  });

  useEffect(() => {
    const checkAuth = async () => {
      const token = getStoredToken();
      if (!token) {
        setState({ isAuthenticated: false, isLoading: false, user: null });
        return;
      }

      setupApiClient();

      try {
        const result = await authGetCurrentUser();
        if (result.data) {
          const user: AuthUser = {
            id: result.data.id,
            username: result.data.username,
            displayName: result.data.displayName,
            email: result.data.email ?? null,
            profilePictureUrl: result.data.profilePictureUrl ?? null,
          };
          setState({ isAuthenticated: true, isLoading: false, user });
        } else {
          clearApiAuth();
          setState({ isAuthenticated: false, isLoading: false, user: null });
        }
      } catch {
        clearApiAuth();
        setState({ isAuthenticated: false, isLoading: false, user: null });
      }
    };

    checkAuth();
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    // Forward any campaign attribution captured from the landing URL. It is
    // only persisted server-side when this login creates a brand-new user.
    const attribution = getStoredAttribution();
    const result = await googleLogin(credential, attribution);

    setApiAuth(result.token);
    // Attribution has been handed to the backend — don't re-send on future
    // logins.
    clearStoredAttribution();
    const user: AuthUser = {
      id: result.user.id,
      username: result.user.username,
      displayName: result.user.displayName,
      email: result.user.email ?? null,
      profilePictureUrl: null,
    };
    setState({ isAuthenticated: true, isLoading: false, user });
  }, []);

  const logout = useCallback(() => {
    clearApiAuth();
    setState({ isAuthenticated: false, isLoading: false, user: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
